/**
 * The persisted inventory document — what is actually in the cupboard
 * (PLAN §3 V1: *"Inventory (stock levels, 'can I brew this?' check, shopping
 * list)"*).
 *
 * ## One item is one stockable thing
 *
 * An {@link InventoryItemDoc} is a *lot*: 500 g of this sack of Citra, at this
 * lot's alpha acid. Two sacks of the same variety are two items, because their
 * lot numbers, their alpha acids and their ages genuinely differ, and averaging
 * them would throw away the only fact per-lot tracking exists to keep.
 *
 * ## Identity: provenance, or a link, or neither
 *
 * `catalogRef` is the identical {@link CatalogRef} a snapshotted recipe item
 * holds — provenance, never a foreign key — plus the display fields
 * ({@link InventoryItemDoc.name}, {@link InventoryItemDoc.maker}) copied at
 * add-time. That is what lets "do I have this?" be answered without a catalog
 * round-trip and keeps a renamed catalog entry from silently renaming the
 * brewer's own label.
 *
 * `ingredientId` is the other kind of identity: a foreign key into the brewer's
 * own library. A linked lot's name and maker are **owned by the ingredient** —
 * the reducer fills them on `inventory.add` and rewrites them when the
 * ingredient is renamed (`customIngredient.update`) — because the two documents
 * used to be joined by nothing but a name string, and for a custom malt with a
 * maker that string never matched (`"Weyermann Pilsner"` on the recipe row,
 * bare `"Pilsner"` here). A lot carries at most one of the two; a fully custom
 * one-off carries neither and sits in the same table.
 *
 * ## Quantity: one number, two units, chosen by kind
 *
 * | Kind | Unit | Why |
 * | --- | --- | --- |
 * | `fermentable` · `hop` · `misc` | **grams** | Everything mass-based is weighed, and grams is the unit a homebrew-scale hop or salt purchase is actually written in. Recipes store kilograms, so the comparison multiplies by 1000 in exactly one place ({@link requiredQuantityFor}). |
 * | `yeast` | **packs** | A brewer owns three sachets, not 34.5 g. Recipes count packs too (`RecipeYeast.packs`), so the units already agree. {@link InventoryItemDoc.gramsPerPack} is carried, nullable, for the day a starter calculator needs it — and is `null` for liquid yeast, which has no mass to speak of. |
 *
 * Grams rather than kilograms is a deliberate departure from the document
 * model's usual canon, and it is confined to this file plus the two conversion
 * points in `../inventory/`. The reason is legibility of the stored record: an
 * inventory row is a number a human types off a scale, and `0.0283495` is not
 * that number. Nothing in `@beerwright/core` ever sees an inventory quantity.
 *
 * ## Per-lot overrides
 *
 * Three, and only where the fact is genuinely per-lot rather than per product:
 * a hop's measured alpha acid off the bag, a yeast's manufacture date (which
 * decides viability, PLAN §5.6), and what this sack cost. Alpha and date are
 * meaningless on the other kinds and always `null` there; {@link InventoryItemDoc.unitCost}
 * applies to every kind. None of them are ever guessed.
 *
 * ## Harvested yeast: a lot with a parent
 *
 * A jar of slurry cropped off a finished batch is a stocked lot like any other
 * — same table, same quantity arithmetic, same availability check — with one
 * extra fact on it: {@link InventoryItemDoc.yeastHarvest}, which says which
 * batch it came out of, on what day, and how many ferments the culture has been
 * through. That is the whole lineage model, and it is deliberately a **single
 * hop backwards**, not a chain stored on the item.
 *
 * The chain is reconstructed rather than duplicated because every link of it
 * already exists: the harvest points at a batch, the batch's
 * `pitchedYeast.generation` says what went into it, and that pitch pointed at
 * an earlier lot. Storing the ancestry on each jar would be four copies of the
 * same history, three of which go stale the moment a batch is renamed.
 * {@link YeastHarvest.batchName} is the one denormalised field, snapshotted for
 * the same reason {@link InventoryItemDoc.name} is: a deleted batch must leave
 * "gen 4, off the October pale" still readable.
 *
 * Viability then decays from {@link YeastHarvest.harvestedAt} rather than from
 * {@link InventoryItemDoc.manufactureDate}, which a slurry does not have. See
 * {@link yeastViabilityDate}, and core's `slurryViability` for the curve and an
 * honest account of how much it is worth.
 */

import { harvestDateForCropYear } from '@beerwright/core';
import { z } from 'zod';
import { INVENTORY_SCHEMA_VERSION } from '../version';
import {
  catalogRefKindSchema,
  catalogRefSchema,
  documentIdSchema,
  hasOneProvenance,
  hopPackagingSchema,
  hopStoragePlaceSchema,
  ingredientLinkSchema,
  isoDateSchema,
  nameSchema,
  nonNegativeSchema,
  notesSchema,
  ONE_PROVENANCE_ISSUE,
  timestampSchema,
} from './common';

// ---------------------------------------------------------------------------
// Kinds and units
// ---------------------------------------------------------------------------

/**
 * What sort of thing is on the shelf.
 *
 * Deliberately the same four values as {@link catalogRefKindSchema} — styles
 * are not stockable, and a fifth "other" bucket would only be a place for
 * things the availability check could not use.
 */
export const inventoryKindSchema = catalogRefKindSchema;

/** @see inventoryKindSchema */
export type InventoryKind = z.infer<typeof inventoryKindSchema>;

/** Every stockable kind, in the order the UI groups them. */
export const INVENTORY_KINDS: readonly InventoryKind[] = [
  'fermentable',
  'hop',
  'yeast',
  'misc',
] as const;

/** The unit a kind's {@link InventoryItemDoc.quantity} counts in. */
export type InventoryUnit = 'g' | 'packs';

/**
 * Which unit a kind is counted in. See this module's header for the table.
 *
 * A function rather than a record so callers read `inventoryUnitOf(item.kind)`
 * at the point of use and cannot accidentally hold a stale unit alongside a
 * changed kind.
 */
export function inventoryUnitOf(kind: InventoryKind): InventoryUnit {
  return kind === 'yeast' ? 'packs' : 'g';
}

// ---------------------------------------------------------------------------
// Quantity arithmetic
// ---------------------------------------------------------------------------

/**
 * Decimal places a stored quantity is rounded to.
 *
 * Six is far finer than any scale a brewer owns (a milligram is 0.001 g) and
 * coarse enough to erase binary floating-point residue. It exists for undo, not
 * for precision: `inventory.adjustQuantity` is inverted by the opposite delta,
 * and `q + d − d` must come back to `q` *exactly* for the package's
 * `apply(invert(s, c), apply(s, c)) === s` property to hold. Rounding both the
 * delta and the result to the same grid makes it exact.
 */
export const QUANTITY_DECIMALS = 6;

const QUANTITY_FACTOR = 10 ** QUANTITY_DECIMALS;

/**
 * Round a quantity or a delta onto the storage grid.
 *
 * Symmetric about zero (`round(−x) === −round(x)`), unlike `Math.round`, whose
 * half-up rule breaks the sign symmetry that undo depends on at exact halves.
 */
export function roundQuantity(value: number): number {
  if (!Number.isFinite(value)) return value;
  const rounded = Math.round(Math.abs(value) * QUANTITY_FACTOR) / QUANTITY_FACTOR;
  return value < 0 ? -rounded : rounded;
}

/**
 * Decimal places a stored unit cost is rounded to.
 *
 * Per-gram costs are small (`$0.50 / lb` is about `0.00110231` / g). Six
 * decimal places cannot represent that without a 0.4999 display on the way
 * back through a pound; eight keeps a typed `$0.50 / lb` and a `$5 for 10 lb`
 * sack both round-tripping to the two-decimal total the brewer paid.
 */
export const UNIT_COST_DECIMALS = 8;

const UNIT_COST_FACTOR = 10 ** UNIT_COST_DECIMALS;

/**
 * Round a unit cost onto the storage grid. Symmetric about zero, same reason
 * {@link roundQuantity} is.
 */
export function roundUnitCost(value: number): number {
  if (!Number.isFinite(value)) return value;
  const rounded = Math.round(Math.abs(value) * UNIT_COST_FACTOR) / UNIT_COST_FACTOR;
  return value < 0 ? -rounded : rounded;
}

// ---------------------------------------------------------------------------
// Yeast lineage
// ---------------------------------------------------------------------------

/**
 * The generation a culture straight out of the lab is on: **0**.
 *
 * Generations count *ferments the culture has been through*, so a smack pack is
 * on none of them, the slurry cropped off the batch it fermented is gen 1, and
 * the slurry off the batch that one was pitched into is gen 2. Brewfather and
 * BeerSmith both count the repitches this way, and it is also the way the
 * question is usually asked out loud: "what generation is that?" means "how
 * many times has it been round?", not "how many packs ago was the lab".
 *
 * The alternative convention — the pack is gen 1 — is defensible and is what
 * some commercial cellar logs use. It is not what this app does, and the two
 * are off by one, which is why the number is written down here rather than
 * assumed at four call sites.
 */
export const LAB_PACK_GENERATION = 0;

/**
 * Where a harvested culture came from.
 *
 * `null` on a lot means yeast that was bought rather than cropped, which is
 * every lot in the cupboard before this existed.
 */
export const yeastHarvestSchema = z.strictObject({
  /**
   * The batch it was cropped from, or `null` once that batch is deleted.
   *
   * A back-link and nothing more, exactly as {@link BatchDoc.recipeId} is: no
   * number on this item is read through it, and a deleted batch leaves the jar
   * entirely intact because `batchName` was copied at harvest time.
   */
  batchId: documentIdSchema.nullable(),
  /** That batch's name on the day of the harvest, snapshotted. */
  batchName: nameSchema,
  /**
   * The day it came out of the fermenter, `YYYY-MM-DD`.
   *
   * **This is what viability decays from**, and it is a separate field from
   * {@link InventoryItemDoc.manufactureDate} rather than a reuse of it because
   * the two are different claims: one is a date a lab printed on a pack, the
   * other is a date the brewer cropped a jar. A slurry has no manufacture date
   * and never will.
   */
  harvestedAt: isoDateSchema,
  /**
   * Ferments this culture has been through. See {@link LAB_PACK_GENERATION}.
   *
   * At least 1: a harvest is by definition downstream of one ferment. The
   * ceiling is a sanity bound, not a limit anyone will meet — the house strains
   * that get repitched forever are commercial, and even they re-isolate.
   */
  generation: z.number().int().min(1).max(1000),
});

/** @see yeastHarvestSchema */
export type YeastHarvest = z.infer<typeof yeastHarvestSchema>;

// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------

/**
 * One stocked lot, before the one-provenance check — the shape the update
 * change set is derived from. Plain, because Zod refuses `.omit()` on a
 * refined object; documents parse with {@link inventoryItemDocSchema} below.
 */
/** The earliest crop year a hop bag is likely to say. */
export const CROP_YEAR_MIN = 1990;

/**
 * The latest crop year the document accepts.
 *
 * A constant, deliberately, where it used to be `new Date().getFullYear() + 1`.
 * This module is evaluated in the sync server's global scope, and a deployed
 * Cloudflare Worker pins `Date.now()` to the Unix epoch there until its first
 * I/O (a Spectre mitigation), so the bound read 1971 on the server and every
 * real crop year failed the envelope check that the browser had just passed.
 * The "not years in the future" check belongs in the dialog that takes the
 * year, where the clock is live; see `edit-lot-dialog.tsx`.
 */
export const CROP_YEAR_MAX = 2100;

/** A crop year as the document stores it: an integer within {@link CROP_YEAR_MIN}–{@link CROP_YEAR_MAX}. */
export const cropYearSchema = z.number().int().min(CROP_YEAR_MIN).max(CROP_YEAR_MAX);

export const inventoryItemFieldsSchema = z.strictObject({
  /** Document schema version; see {@link INVENTORY_SCHEMA_VERSION}. */
  schemaVersion: z.number().int().min(1),
  /** Stable item id, unique within the workspace. */
  id: documentIdSchema,
  /** What sort of thing this is. Decides the quantity's unit. */
  kind: inventoryKindSchema,
  /**
   * Where it came from, or `null` for something the brewer typed in.
   *
   * Provenance, exactly as on a recipe item: nothing dereferences it to read a
   * number. It is what the availability check matches on first, and what lets
   * the catalog browser mark a row "in stock".
   */
  catalogRef: catalogRefSchema.nullable(),
  /**
   * Which of the brewer's own ingredients this is a lot of, or `null`.
   *
   * The reducer owns a linked lot's {@link name} and {@link maker}; see the
   * module header. At most one of this and {@link catalogRef} is set. An id that
   * no longer resolves reads as `null` wherever it is consumed (D-IL8), and is
   * kept rather than cleared, because on a second device the ingredient it
   * names may simply not have arrived yet.
   */
  ingredientId: ingredientLinkSchema,
  /**
   * Display name, snapshotted at add-time — `Citra`, `Weyermann Pilsner`.
   *
   * A copy rather than a lookup so a renamed catalog entry cannot rename what
   * is written on the brewer's own sack, and so a custom item needs no special
   * case.
   */
  name: nameSchema,
  /** Maltster, lab or brand, or `null` where the kind has no maker (hops, salts). */
  maker: z.string().min(1).max(60).nullable(),
  /**
   * How much is held, in {@link inventoryUnitOf} of {@link kind} — **grams**
   * for fermentables, hops and miscs, **packs** for yeast.
   */
  quantity: nonNegativeSchema.max(10_000_000),
  /**
   * Grams in one pack, for yeast, or `null` when unknown (every liquid pack,
   * and any dry sachet whose weight the brewer has not typed in).
   *
   * Carried, never invented: it is what a starter calculation will need
   * (PLAN §5.6) and what a "500 g brick" purchase converts through.
   */
  gramsPerPack: z.number().finite().min(0).max(5000).nullable(),
  /**
   * This lot's measured alpha acid, **% w/w**, or `null` to use the variety's
   * published band. Hops only; meaningless and always `null` elsewhere.
   */
  lotAlphaAcidPercent: z.number().finite().min(0).max(30).nullable(),
  /**
   * Manufacture date as `YYYY-MM-DD`, or `null`. Yeast only — it is what
   * viability decays from.
   */
  manufactureDate: isoDateSchema.nullable(),
  /**
   * When this lot was bought (PLAN-HOP-AGE §A).
   *
   * Every kind, because "is this sack old?" is a fair question about malt and
   * salts too — but only hops have a curve to run from it, so on the other
   * kinds it is shown and never calculated with (D-HA5). Never inferred from
   * {@link InventoryItemDoc.createdAt}, which is when the row was typed and
   * routinely months later than the purchase.
   */
  purchasedAt: isoDateSchema.nullable().default(null),
  /**
   * The harvest year printed on a hop's bag. `null` on every other kind.
   *
   * A year rather than a date because a year is what the bag says. Which month
   * that means is the model's business, not the document's: `core` resolves it
   * from the variety's origin, since the southern hemisphere harvests in March
   * and the northern in September (D-HA3).
   */
  cropYear: cropYearSchema.nullable().default(null),
  /**
   * How *this* lot is kept, when it differs from the workspace default.
   *
   * `null` is the common case and means "however I keep my hops", which is a
   * setting rather than a fact about a lot (D-HA4). Both halves reuse core's
   * vocabulary rather than restating it, because the curve is the only thing
   * that consumes them.
   */
  hopStorage: z
    .strictObject({
      place: hopStoragePlaceSchema,
      packaging: hopPackagingSchema,
    })
    .nullable()
    .default(null),
  /**
   * Where this yeast was cropped from, or `null` for a lot that was bought.
   *
   * Yeast only, and always `null` on the other kinds. `.default(null)` so a lot
   * written before harvesting existed still parses as bought, which is the
   * truth for every one of them — and, because `.partial()` does *not* strip a
   * `.default()`, `inventoryChangesSchema` restates the field without one so an
   * update touching the notes cannot silently un-harvest a jar.
   */
  yeastHarvest: yeastHarvestSchema.nullable().default(null),
  /**
   * What one stored unit of this lot cost, in the workspace currency — **per
   * gram** for fermentables, hops and miscs, **per pack** for yeast — or
   * `null` when unknown.
   *
   * A unit cost rather than a "I paid X for the sack" total so remaining
   * value and brew-day COGS survive quantity movements: using 2 kg of a
   * 25 kg sack must not forget what the rest is worth. The add dialog still
   * asks for a total paid and divides. `.default(null)` so a lot written
   * before the field existed still parses as unpriced.
   */
  unitCost: z.number().finite().nonnegative().max(10_000).nullable().default(null),
  /** Brewer's note: supplier, lot number, where it is stored. */
  notes: notesSchema,
  /** Creation time, **ms since epoch**, from the creating command's envelope. */
  createdAt: timestampSchema,
  /** Last-modification time, **ms since epoch**, from the last command's envelope. */
  updatedAt: timestampSchema,
});

/** One stocked lot. @see inventoryItemFieldsSchema */
export const inventoryItemDocSchema = inventoryItemFieldsSchema.refine(
  hasOneProvenance,
  ONE_PROVENANCE_ISSUE,
);

/** @see inventoryItemDocSchema */
export type InventoryItemDoc = z.infer<typeof inventoryItemDocSchema>;

/**
 * Build an inventory item document.
 *
 * Pure: identity, quantity and time all come from the caller, exactly as
 * {@link buildBatchDoc} does — this package has no clock and mints no ids. The
 * per-kind overrides default to `null` so a caller only names the ones that
 * apply to the kind it is building.
 */
export function buildInventoryItemDoc(args: {
  readonly id: string;
  readonly kind: InventoryKind;
  readonly name: string;
  readonly quantity: number;
  readonly timestamp: number;
  readonly catalogRef?: InventoryItemDoc['catalogRef'];
  readonly ingredientId?: string | null;
  readonly maker?: string | null;
  readonly gramsPerPack?: number | null;
  readonly lotAlphaAcidPercent?: number | null;
  readonly manufactureDate?: string | null;
  readonly purchasedAt?: string | null;
  readonly cropYear?: number | null;
  readonly hopStorage?: InventoryItemDoc['hopStorage'];
  readonly yeastHarvest?: YeastHarvest | null;
  readonly unitCost?: number | null;
  readonly notes?: string;
}): InventoryItemDoc {
  return {
    schemaVersion: INVENTORY_SCHEMA_VERSION,
    id: args.id,
    kind: args.kind,
    catalogRef: args.catalogRef ?? null,
    ingredientId: args.ingredientId ?? null,
    name: args.name,
    maker: args.maker ?? null,
    quantity: roundQuantity(args.quantity),
    gramsPerPack: args.gramsPerPack ?? null,
    lotAlphaAcidPercent: args.lotAlphaAcidPercent ?? null,
    manufactureDate: args.manufactureDate ?? null,
    purchasedAt: args.purchasedAt ?? null,
    cropYear: args.cropYear ?? null,
    hopStorage: args.hopStorage ?? null,
    yeastHarvest: args.yeastHarvest ?? null,
    unitCost: args.unitCost == null ? null : roundUnitCost(args.unitCost),
    notes: args.notes ?? '',
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
  };
}

/**
 * The order two lots of the same thing are consumed in: oldest first, ties
 * broken by id.
 *
 * FIFO is what a brewer does with a shelf, and — the reason it is pinned here —
 * it makes allocation across lots a *deterministic* function of the workspace,
 * which the availability check and the brew-day deduction both depend on to
 * agree with each other on every device.
 */
export function compareInventoryLots(a: InventoryItemDoc, b: InventoryItemDoc): number {
  return lotAgeKey(a) - lotAgeKey(b) || a.id.localeCompare(b.id);
}

/**
 * When a lot counts as having arrived, for {@link compareInventoryLots}.
 *
 * `createdAt` is when the *row was typed*, which is only the same thing as
 * "when the sack arrived" for a brewer who enters stock the day they buy it.
 * A sack that sat in the garage for a year before the cupboard was ever
 * tracked used to sort last, behind hops bought this morning, and a brew day
 * would take from the newer one.
 *
 * A purchase date wins where there is one and `createdAt` stands in where there
 * is not, which keeps the ordering deterministic — the property the availability
 * check and the brew-day deduction both depend on — and changes nothing at all
 * for a workspace that has never recorded a date. A malformed date cannot reach
 * here through the schema, but is treated as absent rather than as `NaN`, which
 * would make the comparator non-transitive.
 */
function lotAgeKey(item: InventoryItemDoc): number {
  if (item.purchasedAt === null) return item.createdAt;
  const parsed = Date.parse(item.purchasedAt);
  return Number.isNaN(parsed) ? item.createdAt : parsed;
}

/**
 * The two dates the hop-ageing curve reads off a lot, or `null` for a kind
 * that has no curve.
 *
 * The counterpart of {@link yeastViabilityDate}, and here for its reason: one
 * function, so the lot row, the recipe prompt and anything later cannot pick
 * different dates for the same sack. Which *curve* they feed is core's
 * decision, not this package's — see `hopAlphaRemainingFromDates` there.
 *
 * `origin` is the variety's country, which the lot itself does not carry: a
 * crop year only names a month once you know which hemisphere picked it. The
 * caller has the catalog entry (or the library one) and passes it through;
 * `null` is honest and resolves to the northern harvest.
 */
export function hopFreshnessDates(
  item: InventoryItemDoc,
  origin: string | null,
): { readonly harvestedAt: string | null; readonly purchasedAt: string | null } | null {
  if (item.kind !== 'hop') return null;
  return {
    harvestedAt: item.cropYear === null ? null : harvestDateForCropYear(item.cropYear, origin),
    purchasedAt: item.purchasedAt,
  };
}

/**
 * The date a yeast lot's viability decays from, or `null` if it has none.
 *
 * A harvest date wins over a manufacture date whenever both are present,
 * because a jar cropped in October from a pack made in March is October's
 * yeast: the pack date describes a population that no longer exists. The
 * harvest flow does not set both, so this only arises on a lot a brewer has
 * edited by hand, but the tie has to break somewhere and this is the way round
 * that cannot be wrong.
 *
 * One function so the row, the availability chip and any future starter
 * integration all pick the same date. Which *curve* that date is fed to is
 * core's decision, not this package's — see `slurryViability` and
 * `packViability` there.
 */
export function yeastViabilityDate(item: InventoryItemDoc): string | null {
  return item.yeastHarvest?.harvestedAt ?? item.manufactureDate;
}
